"use client";

import { useState } from "react";
import { CommonError, SupportedLanguage } from "../types";

const COMMON_ERRORS: CommonError[] = [
  {
    id: "1",
    label: "TypeError: Cannot read property",
    language: "javascript",
    message: "TypeError: Cannot read properties of undefined (reading 'map')",
    slug: "javascript-typeerror-cannot-read-property",
  },
  {
    id: "2",
    label: "SyntaxError: Unexpected token",
    language: "javascript",
    message: "SyntaxError: Unexpected token '<'",
    slug: "javascript-syntaxerror-unexpected-token",
  },
  {
    id: "3",
    label: "IndexError: list index out of range",
    language: "python",
    message: "IndexError: list index out of range",
    slug: "python-indexerror-list-index-out-of-range",
  },
  {
    id: "4",
    label: "AttributeError: NoneType",
    language: "python",
    message: "AttributeError: 'NoneType' object has no attribute 'split'",
    slug: "python-attributeerror-nonetype",
  },
  {
    id: "5",
    label: "Undefined index",
    language: "php",
    message: "Notice: Undefined index: username in /var/www/html/index.php on line 24",
    slug: "php-notice-undefined-index-username",
  },
  {
    id: "6",
    label: "Fatal error: Call on null",
    language: "php",
    message: "Fatal error: Uncaught Error: Call to a member function getId() on null",
    slug: "php-fatal-error-call-on-null",
  },
  {
    id: "7",
    label: "NullPointerException",
    language: "java",
    message: "Exception in thread 'main' java.lang.NullPointerException: Cannot invoke 'String.length()' because 'str' is null",
    slug: "java-nullpointerexception",
  },
  {
    id: "8",
    label: "ORA-00942: table or view does not exist",
    language: "sql",
    message: "ORA-00942: table or view does not exist",
    slug: "sql-ora-00942-table-not-exist",
  },
];

const LANG_COLORS: Record<SupportedLanguage, { bg: string; text: string; border: string }> = {
  javascript: { bg: "bg-yellow-400/10", text: "text-yellow-400", border: "border-yellow-400/20" },
  php: { bg: "bg-indigo-400/10", text: "text-indigo-400", border: "border-indigo-400/20" },
  python: { bg: "bg-blue-400/10", text: "text-blue-400", border: "border-blue-400/20" },
  java: { bg: "bg-orange-400/10", text: "text-orange-400", border: "border-orange-400/20" },
  sql: { bg: "bg-emerald-400/10", text: "text-emerald-400", border: "border-emerald-400/20" },
  other: { bg: "bg-zinc-400/10", text: "text-zinc-400", border: "border-zinc-400/20" },
};

const LANG_SHORT: Record<SupportedLanguage, string> = {
  javascript: "JS",
  php: "PHP",
  python: "PY",
  java: "JV",
  sql: "SQL",
  other: "?",
};

interface CommonErrorsPanelProps {
  onQuickPick: (msg: string, lang: SupportedLanguage) => void;
}

export default function CommonErrorsPanel({ onQuickPick }: CommonErrorsPanelProps) {
  const [activeFilter, setActiveFilter] = useState<SupportedLanguage | "all">("all");

  const filtered =
    activeFilter === "all"
      ? COMMON_ERRORS
      : COMMON_ERRORS.filter((e) => e.language === activeFilter);

  const filters: (SupportedLanguage | "all")[] = ["all", "javascript", "python", "php", "java", "sql"];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="px-4 py-3.5 border-b border-zinc-800 flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-500">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
        <h3 className="text-sm font-semibold text-zinc-300">Common Errors</h3>
        <span className="ml-auto text-xs text-zinc-600 bg-zinc-800 px-1.5 py-0.5 rounded">
          {filtered.length}
        </span>
      </div>

      {/* Language Filter Tabs */}
      <div className="flex gap-1 p-2 border-b border-zinc-800 overflow-x-auto">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            className={`flex-shrink-0 text-xs px-2 py-1 rounded-md font-medium transition-all duration-150 ${
              activeFilter === f
                ? "bg-zinc-700 text-zinc-200" :"text-zinc-600 hover:text-zinc-400 hover:bg-zinc-800/50"
            }`}
            style={{ fontFamily: f !== "all" ? "'IBM Plex Mono', monospace" : undefined }}
          >
            {f === "all" ? "All" : LANG_SHORT[f]}
          </button>
        ))}
      </div>

      {/* Error List */}
      <div className="divide-y divide-zinc-800/60">
        {filtered.map((error) => {
          const colors = LANG_COLORS[error.language];
          return (
            <button
              key={error.id}
              onClick={() => onQuickPick(error.message, error.language)}
              className="w-full text-left px-4 py-3 hover:bg-zinc-800/40 transition-colors duration-150 group flex items-start gap-2.5"
            >
              <span
                className={`flex-shrink-0 text-xs font-bold px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border} mt-0.5`}
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px" }}
              >
                {LANG_SHORT[error.language]}
              </span>
              <span className="text-xs text-zinc-400 group-hover:text-zinc-300 transition-colors leading-5 text-left">
                {error.label}
              </span>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-zinc-700 group-hover:text-zinc-500 flex-shrink-0 ml-auto mt-1 transition-colors"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          );
        })}
      </div>

      {/* SEO Links */}
      <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-900/30">
        <p className="text-xs text-zinc-600 mb-2">Error reference pages</p>
        <div className="space-y-1">
          {COMMON_ERRORS.slice(0, 3).map((e) => (
            <a
              key={e.id}
              href={`/errors/${e.slug}`}
              className="block text-xs text-zinc-600 hover:text-cyan-400 transition-colors truncate"
            >
              → /errors/{e.slug}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}